/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useElementFilter } from '@backstage/core-plugin-api';
import { Grid, makeStyles, Tooltip, Typography } from '@material-ui/core';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import React from 'react';

const useStyles = makeStyles(theme => ({
  value: {
    fontWeight: 'bold',
    overflow: 'hidden',
    lineHeight: '24px',
    wordBreak: 'break-word',
  },
  label: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: '10px',
    fontWeight: 'bold',
    letterSpacing: 0.5,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  // Bigger than the 10px label, or it is a smudge rather than an icon, and
  // lifted to sit on the cap height of the uppercase label next to it.
  labelIcon: {
    fontSize: '13px',
    marginLeft: theme.spacing(0.5),
    verticalAlign: 'text-top',
    cursor: 'help',
  },
}));

/**
 * Props for {@link AboutField}.
 *
 * @public
 */
export interface AboutFieldProps {
  label: string;
  value?: string;
  /**
   * Shown on hover, for a `value` that is short at the cost of being exact.
   * Ignored when the field renders `children` instead.
   */
  title?: string;
  /**
   * What the row means, hung off an info icon next to the label. For a row
   * whose label needs a sentence to be understood - unlike `title`, which
   * explains the value rather than the field.
   */
  description?: string;
  gridSizes?: Record<string, number>;
  children?: React.ReactNode;
}

/** @public */
export function AboutField(props: Readonly<AboutFieldProps>) {
  const { label, value, title, description, gridSizes, children } = props;
  const classes = useStyles();

  const childElements = useElementFilter(children, c => c.getElements());

  // Sits inside the Typography rather than around it: that one is a block and
  // fills the whole grid cell, so a tooltip hung off it lands next to the cell
  // instead of next to the text. The span is inline and hugs the value.
  // Children bring their own layout and are left as they are.
  const valueText = title ? (
    <Tooltip title={title}>
      <span>{value ?? `unknown`}</span>
    </Tooltip>
  ) : (
    (value ?? `unknown`)
  );

  // Content is either children or a string prop `value`
  const content =
    childElements.length > 0 ? (
      childElements
    ) : (
      <Typography variant="body2" className={classes.value}>
        {valueText}
      </Typography>
    );
  return (
    <Grid item {...gridSizes}>
      <Typography variant="h2" className={classes.label}>
        {label}
        {description && (
          <Tooltip title={description}>
            {/* Named with `aria-label`, not `titleAccess`: that one renders an
                svg `<title>`, which the browser shows as a tooltip of its own
                on top of this one. The icon is the whole content of the
                element, so it is not hidden from screen readers either. */}
            <InfoOutlined
              className={classes.labelIcon}
              role="img"
              aria-hidden={false}
              aria-label={description}
            />
          </Tooltip>
        )}
      </Typography>
      {content}
    </Grid>
  );
}
